import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { API_URL } from '../config';
import { toast } from 'react-toastify';
import WalletCard from './WalletCard';

function WorkerDashboard() {
    const [availableTasks, setAvailableTasks] = useState([]);
    const [assignedTasks, setAssignedTasks] = useState([]);
    const [submittedTasks, setSubmittedTasks] = useState([]);
    const [reputation, setReputation] = useState(null); 
    const [loading, setLoading] = useState(true);
    const [proofUrl, setProofUrl] = useState('');
    
    const navigate = useNavigate();
    const location = useLocation();

    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    const userEmail = localStorage.getItem('email');

    useEffect(() => {
        if (!token || role !== 'WORKER') {
            navigate('/');
            return;
        }
        fetchTasks();
        fetchReputation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchTasks = async () => {
        try {
            const availRes = await axios.get(`${API_URL}/api/worker/tasks/available`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAvailableTasks(availRes.data.content || availRes.data);

            const assignedRes = await axios.get(`${API_URL}/api/worker/tasks/assigned`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAssignedTasks(assignedRes.data.content || assignedRes.data);

            setLoading(false);
        } catch (err) {
            console.error('Error fetching tasks:', err);
            setLoading(false);
        }
    };

    const fetchReputation = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/worker/reputation`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReputation(res.data);
        } catch (err) {
            console.log('Reputation data not available yet:', err);
        }
    };

    const handleAccept = async (taskId) => {
        try {
            await axios.post(
                `${API_URL}/api/worker/tasks/${taskId}/accept`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Task accepted successfully!');
            fetchTasks();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to accept task');
        }
    };

    const handleSubmitProof = async (taskId) => {
        if (!proofUrl) {
            toast.error('Please enter a proof URL');
            return;
        }

        try {
            await axios.post(
                `${API_URL}/api/worker/submissions?taskId=${taskId}&proofType=URL&proofUrl=${proofUrl}&notes=Submitted%20proof`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Proof submitted successfully!');
            setProofUrl('');
            setSubmittedTasks([...submittedTasks, taskId]);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to submit proof');
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/');
    };

    const getTierBadgeClass = (tier) => {
        switch(tier) {
            case 'PLATINUM': return 'badge badge-error';
            case 'GOLD': return 'badge badge-warning';
            case 'TRUSTED': return 'badge badge-success';
            default: return 'badge';
        }
    };

    // --- SKELETON LOADER ---
    if (loading) {
        return (
            <div className="dashboard-layout">
                <aside className="sidebar">
                    <Link to="/" className="sidebar-brand">infloir</Link>
                    <nav className="sidebar-nav">
                        <div className="nav-item" style={{ opacity: 0.3 }}><span>📊</span> Overview</div>
                        <div className="nav-item" style={{ opacity: 0.3 }}><span>🏆</span> Reputation</div>
                        <div className="nav-item" style={{ opacity: 0.3 }}><span>💰</span> Wallet</div>
                        <div className="nav-item" style={{ opacity: 0.3 }}><span>👤</span> Profile</div>
                    </nav>
                </aside>
                <div className="main-content">
                    <header className="topbar">
                        <div style={{ width: '150px', height: '20px', background: 'var(--bg-hover)', borderRadius: '4px' }}></div>
                    </header>
                    <main className="page-container">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '32px' }}>
                            <div className="card" style={{ height: '200px', background: 'var(--bg-hover)', animation: 'pulse 2s infinite' }}></div>
                            <div className="card" style={{ height: '200px', background: 'var(--bg-hover)', animation: 'pulse 2s infinite' }}></div>
                        </div>
                        <div style={{ height: '30px', width: '200px', background: 'var(--bg-hover)', borderRadius: '4px', marginBottom: '24px', animation: 'pulse 2s infinite' }}></div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                            {[1, 2, 3].map(i => (
                                <div key={i} className="card" style={{ height: '220px', background: 'var(--bg-hover)', animation: 'pulse 2s infinite' }}></div>
                            ))}
                        </div>
                    </main>
                </div>
            </div>
        );
    }

    const isSuspended = reputation?.suspendedUntil && new Date(reputation.suspendedUntil) > new Date();

    return (
        <div className="dashboard-layout">
            <aside className="sidebar">
                <Link to="/" className="sidebar-brand">infloir</Link>
                <nav className="sidebar-nav">
                    <Link to="/worker-dashboard" className={`nav-item ${location.pathname === '/worker-dashboard' ? 'active' : ''}`}>
                        <span>📊</span> Overview
                    </Link>
                    {/* RE-ADDED REPUTATION LINK */}
                    <Link to="/worker/reputation" className={`nav-item ${location.pathname === '/worker/reputation' ? 'active' : ''}`}>
                        <span>🏆</span> Reputation
                    </Link>
                    <Link to="/wallet" className="nav-item">
                        <span>💰</span> Wallet
                    </Link>
                    <Link to="/profile" className="nav-item">
                        <span>👤</span> Profile
                    </Link>
                </nav>
                
                <div style={{ marginTop: 'auto' }}>
                    <button onClick={handleLogout} className="nav-item" style={{ width: '100%', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer' }}>
                        <span>🚪</span> Log out
                    </button>
                </div>
            </aside>

            <div className="main-content">
                <header className="topbar">
                    <h2 style={{ fontSize: '18px', fontWeight: '500' }}>Worker Dashboard</h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        {isSuspended ? (
                            <span className="badge badge-error">Suspended</span>
                        ) : (
                            <span className="badge badge-success">Online</span>
                        )}
                        <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{userEmail}</span>
                    </div>
                </header>

                <main className="page-container page-fade-in">
                    
                    {isSuspended && (
                        <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid var(--error)', padding: '16px', borderRadius: '8px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <span style={{ fontSize: '24px' }}>⚠️</span>
                            <div>
                                <h4 style={{ color: 'var(--error)', marginBottom: '4px', fontSize: '16px' }}>Account Suspended</h4>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '13px', margin: 0 }}>
                                    Your account is temporarily suspended. You cannot accept new tasks until {new Date(reputation.suspendedUntil).toLocaleString()}.
                                </p>
                            </div>
                        </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '32px' }}>
                        <WalletCard />
                        
                        <div className="card" style={{ padding: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <h3 style={{ fontSize: '16px', marginBottom: '16px', color: 'var(--text)' }}>Worker Status</h3>
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Current Tier</span>
                                <span className={getTierBadgeClass(reputation?.tier)} style={{ fontSize: '14px', fontWeight: 'bold' }}>
                                    {reputation?.tier || 'NEW'}
                                </span>
                            </div>
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Approval Rating</span>
                                <span style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--success)' }}>
                                    {reputation?.approvalRate ? `${reputation.approvalRate.toFixed(1)}%` : '100%'}
                                </span>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Honor Score</span>
                                <span style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--info)' }}>
                                    {reputation?.honorScore || 0}
                                </span>
                            </div>
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Tasks Completed</span>
                                <span style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--text)' }}>
                                    {reputation?.completedTasks || 0}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div style={{ marginBottom: '32px' }}>
                        <h3 style={{ fontSize: '18px', marginBottom: '16px', color: 'var(--text)' }}>Available Tasks</h3>
                        
                        {availableTasks.length === 0 ? (
                            <div className="card" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                                <p style={{ fontSize: '32px', marginBottom: '12px' }}>📭</p>
                                <p>No tasks available right now. Check back later!</p>
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                                {availableTasks.map(task => (
                                    <div key={task.id} className="card card-hover">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                            <h4 style={{ fontSize: '16px', color: 'var(--text)' }}>{task.title}</h4>
                                            <span className="badge badge-success">${task.reward}</span>
                                        </div>
                                        <p style={{ fontSize: '13px', marginBottom: '16px', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                            {task.description}
                                        </p>

                                        {task.assetUrl && (
                                            <div style={{ marginBottom: '16px' }}>
                                                <a href={task.assetUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px', padding: '8px 12px' }}>
                                                    🔗 View Task Assets
                                                </a>
                                            </div>
                                        )}
                                        
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
                                            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Due: {task.deadline}</span>
                                            <button 
                                                onClick={() => handleAccept(task.id)} 
                                                className="btn-primary" 
                                                style={{ padding: '6px 12px', fontSize: '13px' }}
                                                disabled={isSuspended}
                                            >
                                                {isSuspended ? 'Suspended' : 'Accept Task'}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <h3 style={{ fontSize: '18px', marginBottom: '16px', color: 'var(--text)' }}>My Assigned Tasks</h3>
                        
                        {assignedTasks.length === 0 ? (
                            <div className="card" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                                <p style={{ fontSize: '32px', marginBottom: '12px' }}>📋</p>
                                <p>You haven't accepted any tasks yet.</p>
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                                {assignedTasks.map(task => (
                                    <div key={task.id} className="card">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                            <h4 style={{ fontSize: '16px', color: 'var(--text)' }}>{task.title}</h4>
                                            <span className="badge">{task.status}</span>
                                        </div>
                                        <p style={{ fontSize: '13px', marginBottom: '8px' }}>{task.description}</p>
                                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Reward: <strong style={{ color: 'var(--success)' }}>${task.reward}</strong></p>
                                        
                                        {task.assetUrl && (
                                            <div style={{ marginTop: '12px' }}>
                                                <a href={task.assetUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px', padding: '8px 12px' }}>
                                                    🔗 View Task Assets
                                                </a>
                                            </div>
                                        )}

                                        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
                                            {submittedTasks.includes(task.id) ? (
                                                <div style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)', padding: '12px', borderRadius: 'var(--radius-md)' }}>
                                                    <p style={{ fontSize: '13px', color: 'var(--success)', marginBottom: '4px' }}>✅ Proof submitted</p>
                                                    <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Waiting for business review.</p>
                                                </div>
                                            ) : (
                                                task.status === 'ASSIGNED' && (
                                                    <div>
                                                        <label className="form-label" style={{ fontSize: '12px' }}>Submit Proof URL</label>
                                                        <input type="text" placeholder="https://..." value={proofUrl} onChange={(e) => setProofUrl(e.target.value)} style={{ marginBottom: '12px' }} />
                                                        <button onClick={() => handleSubmitProof(task.id)} className="btn-primary" style={{ width: '100%', padding: '8px', fontSize: '13px' }}>
                                                            Submit Proof
                                                        </button>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                </main>
            </div>
        </div>
    );
}

export default WorkerDashboard;